import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DescriptionTemplateStore } from '../types/descriptionTemplate';
import { supabase } from '../utils/supabase';
import { useAuthStore } from './authStore';

const DEFAULT_TEMPLATE_CONTENTS = {
  new: '',
  'like-new': '',
  good: '',
  fair: '',
  poor: '',
  bad: ''
};

export const useDescriptionTemplateStore = create<DescriptionTemplateStore>()(
  persist(
    (set, get) => ({
      templates: [],
      activeTemplateId: null,

      loadTemplates: async () => {
        const user = useAuthStore.getState().user;
        if (!user) return;
        
        try {
          // Fetch templates from Supabase
          const { data, error } = await supabase
            .from('description_templates')
            .select('*')
            .eq('user_id', user.id);
          
          if (error) {
            console.error('Failed to load description templates:', error);
            return;
          }
          
          // Map the database records to the expected format
          const templates = data.map(record => ({
            id: record.id,
            name: record.name,
            contents: record.contents,
            createdAt: record.created_at,
            updatedAt: record.updated_at
          }));
          
          set({ templates });
        } catch (error) {
          console.error('Error loading description templates:', error);
          // Gracefully handle the error by setting an empty array
          // This prevents the error from propagating up and crashing the app
          set({ templates: [] });
        }
      },

      addTemplate: async (name, contents) => {
        const user = useAuthStore.getState().user;
        if (!user) return;
        
        const template = {
          id: crypto.randomUUID(),
          name,
          contents: { ...contents },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        try {
          // Insert into Supabase
          const { error } = await supabase
            .from('description_templates')
            .insert({
              id: template.id,
              name: template.name,
              contents: template.contents,
              user_id: user.id,
              created_at: template.createdAt,
              updated_at: template.updatedAt
            });

          if (error) throw error;
          
          set(state => ({
            templates: [template, ...state.templates],
            activeTemplateId: template.id
          }));
        } catch (error) {
          console.error('Failed to add description template:', error);
        }
      },

      updateTemplate: async (id, updates) => {
        const user = useAuthStore.getState().user;
        if (!user) return;
        
        try {
          const supabaseUpdates = {
            ...updates,
            updated_at: new Date().toISOString()
          };

          // Update in Supabase
          const { error } = await supabase
            .from('description_templates')
            .update(supabaseUpdates)
            .eq('id', id);

          if (error) throw error;
          
          set(state => ({
            templates: state.templates.map(template =>
              template.id === id
                ? {
                    ...template,
                    ...updates,
                    updatedAt: new Date().toISOString()
                  }
                : template
            )
          }));
        } catch (error) {
          console.error('Failed to update description template:', error);
        }
      },

      deleteTemplate: async (id) => {
        const user = useAuthStore.getState().user;
        if (!user) return;
        
        try {
          // Delete from Supabase
          const { error } = await supabase
            .from('description_templates')
            .delete()
            .eq('id', id);

          if (error) throw error;
          
          set(state => ({
            templates: state.templates.filter(t => t.id !== id),
            activeTemplateId: state.activeTemplateId === id ? null : state.activeTemplateId
          }));
        } catch (error) {
          console.error('Failed to delete description template:', error);
        }
      },

      setActiveTemplate: (id) => {
        set({ activeTemplateId: id });
      },

      getTemplateByCondition: (condition) => {
        const { templates, activeTemplateId } = get();
        const activeTemplate = templates.find(t => t.id === activeTemplateId);
        if (!activeTemplate) return '';
        
        return activeTemplate.contents[condition as keyof typeof DEFAULT_TEMPLATE_CONTENTS] || '';
      },

      replaceTemplates: (templates) => {
        set({ 
          templates,
          activeTemplateId: null
        });
      }
    }),
    {
      name: 'description-templates-storage'
    }
  )
);